import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  Grid,
  Paper,
  Divider,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  Sync as SyncIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Storage as StorageIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { projectService } from '../../services/projectService';

interface SyncStatus {
  id: number;
  project_code: string;
  sync_type: string;
  status: string;
  started_at: string;
  completed_at?: string;
  error_message?: string;
  items_processed: number;
  items_total: number;
  initiated_by: string;
}

interface VectorStoreStats {
  total_contexts: number;
  total_documents: number;
  total_projects: number;
  projects: Record<string, { contexts: number; documents: number }>;
  last_updated: string;
}

interface ProjectSummary {
  project_code: string;
  context_count: number;
  document_count: number;
  last_updated?: string;
}

export interface VectorStoreSyncHandle {
  openBulkSyncDialog: () => void;
  openCleanupDialog: (projectCode?: string) => void;
}

const VectorStoreSync = forwardRef<VectorStoreSyncHandle>((_, ref) => {
  const [syncStatuses, setSyncStatuses] = useState<SyncStatus[]>([]);
  const [projectSummaries, setProjectSummaries] = useState<ProjectSummary[]>([]);
  const [vectorStoreStats, setVectorStoreStats] = useState<VectorStoreStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [liveUpdates, setLiveUpdates] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const [bulkSyncDialog, setBulkSyncDialog] = useState(false);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [syncType, setSyncType] = useState<string>('incremental');
  const [error, setError] = useState<string | null>(null);
  const [cleanupDialog, setCleanupDialog] = useState(false);
  const [cleanupProject, setCleanupProject] = useState<string>('');

  useImperativeHandle(
    ref,
    () => ({
      openBulkSyncDialog: () => setBulkSyncDialog(true),
      openCleanupDialog: (projectCode?: string) => {
        setCleanupProject(projectCode || '');
        setCleanupDialog(true);
      },
    }),
    []
  );

  useEffect(() => {
    loadData();
  }, []);

  // Server-push (SSE) for sync status updates.
  useEffect(() => {
    let active = true;
    const abortController = new AbortController();
    let reconnectTimer: number | null = null;

    const apiBase = process.env.REACT_APP_API_URL || '/api';
    const streamUrl = `${apiBase}/project-context/sync-status/stream?limit=100&interval_ms=1500`;

    const readSseStream = async (
      body: ReadableStream<Uint8Array>,
      onEvent: (eventName: string, data: string) => void
    ) => {
      const reader = body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (active) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx = buffer.indexOf('\n\n');
        while (idx !== -1) {
          const rawEvent = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          idx = buffer.indexOf('\n\n');

          const lines = rawEvent.split(/\r?\n/);
          let eventName = 'message';
          const dataLines: string[] = [];

          for (const line of lines) {
            if (!line || line.startsWith(':')) continue;
            if (line.startsWith('event:')) {
              eventName = line.slice('event:'.length).trim() || 'message';
              continue;
            }
            if (line.startsWith('data:')) {
              dataLines.push(line.slice('data:'.length).trimStart());
            }
          }

          const data = dataLines.join('\n');
          if (data) onEvent(eventName, data);
        }
      }
    };

    const connect = async () => {
      if (!active) return;
      setLiveUpdates('connecting');

      try {
        const token = localStorage.getItem('access_token');
        const headers: Record<string, string> = {};
        if (token) headers.Authorization = `Bearer ${token}`;

        const resp = await fetch(streamUrl, {
          method: 'GET',
          headers,
          signal: abortController.signal,
        });

        if (!resp.ok) {
          throw new Error(`Sync status stream failed (${resp.status})`);
        }
        if (!resp.body) {
          throw new Error('Sync status stream has no body');
        }

        setLiveUpdates('connected');

        await readSseStream(resp.body, (eventName, data) => {
          if (eventName === 'sync-statuses') {
            try {
              const payload = JSON.parse(data);
              if (payload?.sync_statuses) {
                setSyncStatuses(payload.sync_statuses);
              }
            } catch (e) {
              console.error('Failed to parse sync-statuses event', e);
            }
          } else if (eventName === 'error') {
            // Keep UI quiet; log for debugging.
            console.warn('Sync status stream error event:', data);
          }
        });

        // If we exit the loop naturally, treat as disconnect and retry.
        if (active) {
          setLiveUpdates('disconnected');
          reconnectTimer = window.setTimeout(connect, 3000);
        }
      } catch (e) {
        if (!active || abortController.signal.aborted) return;
        console.warn('Sync status stream disconnected; retrying...', e);
        setLiveUpdates('disconnected');
        reconnectTimer = window.setTimeout(connect, 3000);
      }
    };

    connect();

    return () => {
      active = false;
      abortController.abort();
      if (reconnectTimer) window.clearTimeout(reconnectTimer);
    };
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadSyncStatuses(),
        loadProjectSummaries(),
        loadVectorStoreStats()
      ]);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadSyncStatuses = async () => {
    try {
      const response = await projectService.getSyncStatuses();
      setSyncStatuses(response.sync_statuses || []);
    } catch (err) {
      console.error('Failed to load sync statuses:', err);
    }
  };

  const loadProjectSummaries = async () => {
    try {
      const summaries = await projectService.getProjectSummaries();
      setProjectSummaries(summaries);
    } catch (err) {
      console.error('Failed to load project summaries:', err);
    }
  };

  const loadVectorStoreStats = async () => {
    try {
      const stats = await projectService.getVectorStoreStats();
      setVectorStoreStats(stats);
    } catch (err) {
      console.error('Failed to load vector store stats:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    setRefreshing(false);
  };

  const handleSyncProject = async (projectCode: string) => {
    try {
      // Use the currently selected sync type so admins can force document_only/full when needed.
      await projectService.syncProject(projectCode, syncType as any, false, 'admin');
      await loadSyncStatuses();
    } catch (err) {
      setError(`Failed to sync project ${projectCode}`);
    }
  };

  const handleBulkSync = async () => {
    if (selectedProjects.length === 0) {
      setError('Please select at least one project');
      return;
    }

    try {
      await projectService.bulkSyncProjects({
        project_codes: selectedProjects,
        sync_type: syncType as any,
        initiated_by: 'admin'
      });
      setBulkSyncDialog(false);
      setSelectedProjects([]);
      await loadSyncStatuses();
    } catch (err) {
      setError('Failed to initiate bulk sync');
    }
  };

  const handleCancelSync = async (syncId: number) => {
    try {
      await projectService.cancelSync(syncId);
      await loadSyncStatuses();
    } catch (err) {
      setError('Failed to cancel sync');
    }
  };

  const handleCleanup = async () => {
    try {
      await projectService.cleanupVectorStore(cleanupProject || undefined);
      setCleanupDialog(false);
      setCleanupProject('');
      await loadVectorStoreStats();
    } catch (err) {
      setError('Failed to cleanup vector store');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      case 'in_progress':
        return <CircularProgress size={20} />;
      case 'pending':
        return <PendingIcon color="warning" />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'in_progress':
        return 'primary';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getProgress = (sync: SyncStatus) => {
    if (sync.items_total === 0) return 0;
    return (sync.items_processed / sync.items_total) * 100;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header with actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Typography variant="h5" component="h2">
            Vector Store Synchronization
          </Typography>
          <Chip
            size="small"
            label={
              liveUpdates === 'connected'
                ? 'Live updates'
                : liveUpdates === 'connecting'
                  ? 'Connecting…'
                  : 'Offline'
            }
            color={liveUpdates === 'connected' ? 'success' : liveUpdates === 'connecting' ? 'info' : 'default'}
            variant="outlined"
          />
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{ mr: 1 }}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="contained"
            startIcon={<SyncIcon />}
            onClick={() => setBulkSyncDialog(true)}
            sx={{ mr: 1 }}
          >
            Bulk Sync
          </Button>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => setCleanupDialog(true)}
          >
            Cleanup
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Vector Store Statistics */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <StorageIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Vector Store Stats</Typography>
              </Box>
              {vectorStoreStats ? (
                <Box>
                  <Typography variant="body2" color="textSecondary">
                    Total Projects: {vectorStoreStats.total_projects}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Contexts: {vectorStoreStats.total_contexts}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Documents: {vectorStoreStats.total_documents}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Last Updated: {formatDate(vectorStoreStats.last_updated)}
                  </Typography>
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  Loading stats...
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Sync Operations */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <TimelineIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Recent Sync Operations</Typography>
              </Box>
              <List>
                {syncStatuses.slice(0, 5).map((sync) => (
                  <ListItem key={sync.id} divider>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center">
                          {getStatusIcon(sync.status)}
                          <Typography variant="subtitle1" sx={{ ml: 1 }}>
                            {sync.project_code}
                          </Typography>
                          <Chip
                            label={sync.status}
                            color={getStatusColor(sync.status)}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {sync.sync_type} sync • Started: {formatDate(sync.started_at)}
                          </Typography>
                          {sync.status === 'in_progress' && (
                            <Box sx={{ mt: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={getProgress(sync)}
                                sx={{ mb: 0.5 }}
                              />
                              <Typography variant="caption" color="textSecondary">
                                {sync.items_processed} / {sync.items_total} items
                              </Typography>
                            </Box>
                          )}
                          {sync.error_message && (
                            <Typography variant="body2" color="error" sx={{ mt: 0.5 }}>
                              Error: {sync.error_message}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {sync.status === 'in_progress' && (
                        <Tooltip title="Cancel Sync">
                          <IconButton
                            edge="end"
                            onClick={() => handleCancelSync(sync.id)}
                            size="small"
                          >
                            <StopIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {syncStatuses.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary="No sync operations found"
                      secondary="Start a sync operation to see status here"
                    />
                  </ListItem>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Project List with Sync Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Projects Available for Sync
              </Typography>
              <List>
                {projectSummaries.map((project) => (
                  <ListItem key={project.project_code} divider>
                    <ListItemText
                      primary={project.project_code}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {project.context_count} contexts • {project.document_count} documents
                          </Typography>
                          {project.last_updated && (
                            <Typography variant="caption" color="textSecondary">
                              Last updated: {formatDate(project.last_updated)}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleSyncProject(project.project_code)}
                        disabled={syncStatuses.some(
                          s => s.project_code === project.project_code && s.status === 'in_progress'
                        )}
                      >
                        Sync
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bulk Sync Dialog */}
      <Dialog open={bulkSyncDialog} onClose={() => setBulkSyncDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Synchronization</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Sync Type</InputLabel>
              <Select
                value={syncType}
                label="Sync Type"
                onChange={(e) => setSyncType(e.target.value)}
              >
                <MenuItem value="incremental">Incremental</MenuItem>
                <MenuItem value="full">Full</MenuItem>
                <MenuItem value="document_only">Document Only</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Typography variant="subtitle1" gutterBottom>
            Select Projects to Sync:
          </Typography>
          <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto', p: 1 }}>
            <List dense>
              {projectSummaries.map((project) => (
                <ListItem
                  key={project.project_code}
                  button
                  onClick={() => {
                    if (selectedProjects.includes(project.project_code)) {
                      setSelectedProjects(selectedProjects.filter(p => p !== project.project_code));
                    } else {
                      setSelectedProjects([...selectedProjects, project.project_code]);
                    }
                  }}
                  selected={selectedProjects.includes(project.project_code)}
                >
                  <ListItemText
                    primary={project.project_code}
                    secondary={`${project.context_count} contexts, ${project.document_count} documents`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>

          {selectedProjects.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Selected: {selectedProjects.join(', ')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkSyncDialog(false)}>Cancel</Button>
          <Button
            onClick={handleBulkSync}
            variant="contained"
            disabled={selectedProjects.length === 0}
          >
            Start Bulk Sync
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cleanup Dialog */}
      <Dialog open={cleanupDialog} onClose={() => setCleanupDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cleanup Vector Store</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action will permanently delete vector store data. This cannot be undone.
          </Alert>
          
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Project to Cleanup (Optional)</InputLabel>
            <Select
              value={cleanupProject}
              label="Project to Cleanup (Optional)"
              onChange={(e) => setCleanupProject(e.target.value)}
            >
              <MenuItem value="">All Projects</MenuItem>
              {projectSummaries.map((project) => (
                <MenuItem key={project.project_code} value={project.project_code}>
                  {project.project_code}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="textSecondary">
            {cleanupProject 
              ? `This will delete all vector store data for project ${cleanupProject}.`
              : 'This will delete ALL vector store data for all projects.'
            }
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCleanupDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCleanup}
            variant="contained"
            color="warning"
          >
            Cleanup
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default VectorStoreSync;
